import { computed, effect, inject, Injectable, Injector, signal, untracked } from '@angular/core';
import { Router } from '@angular/router';
import { StatusIndicatorService } from '@complexcare/harmony';
import { catchError, firstValueFrom, of } from 'rxjs';
import { AdminApi } from '../api';
import { AuthSessionStatus } from '../auth/auth-core/models/auth-session.model';
import { createEmptyUserProfile, type UserProfile } from '../auth/auth-core/models/user-profile.model';
import { AuthStateService } from '../auth/auth-core/services/auth-state.service';
import { AuthorizationService } from '../auth/authorization/services/authorization.service';
import {
  clearSession,
  clearToken,
  loadOfflineUserProfile,
  loadToken,
  saveOfflineUserProfile,
  saveOfflineAuthorizationContext,
  loadOfflineAuthorizationContext,
} from '../auth/session-sync/utils/session-storage.util';
import { decodeJwt } from '../auth/auth-core/utils/jwt.util';
import { ShellAuthLifecycleService } from '../auth/shell-orchestrator/services/shell-auth-lifecycle.service';
import { IndexedDBStore } from '../storage/indexed-db.store';
import { LOOKUP_API_BASE_URL } from '../http/api-client.tokens';
import { DEFAULT_OFFLINE_PIN, OFFLINE_SESSION_DURATION_HOURS } from './offline-auth.tokens';
import { checkNetworkOnline } from './network-connectivity.util';

// ─── Constants ────────────────────────────────────────────────────────────────

export const OFFLINE_RETURN_URL_KEY = 'offline-auth.returnUrl';

const OFFLINE_SESSION_TABLE = 'offline-session';
const OFFLINE_PIN_TABLE = 'offline-pin';

// ─── Models ───────────────────────────────────────────────────────────────────

export interface OfflineSession {
  id: string; // always 'current'
  createdAt: string;
  expiresAt: string;
  userId?: string;
  userProfile?: UserProfile;
}

interface OfflinePinRecord {
  userId: string;
  hash: string;
  /** Server-provided salt used when hashing. Cached here so offline verification works without a network call. */
  salt: string;
  createdAt: string;
  lastSyncedAt?: string;
}

// ─── Service ─────────────────────────────────────────────────────────────────

/**
 * OfflineAuthService
 *
 * Single service managing all offline authentication concerns:
 *
 * - IndexedDB session (configurable duration, default 5 hours after PIN login)
 * - PIN storage and verification (SHA-256 hashed with salt in IndexedDB)
 * - Server PIN synchronization (API-first update; proactive pull on startup)
 * - Online/offline transition monitoring + token expiry handling
 * - Assessment pre-logout auto-save flush
 * - Reactive auth state signals
 */
@Injectable({ providedIn: 'root' })
export class OfflineAuthService {
  private readonly store = inject(IndexedDBStore);
  private readonly authState = inject(AuthStateService);
  private readonly authz = inject(AuthorizationService);
  private readonly statusIndicator = inject(StatusIndicatorService);
  private readonly router = inject(Router);
  private readonly defaultPin = inject(DEFAULT_OFFLINE_PIN);
  private readonly sessionDurationHours = inject(OFFLINE_SESSION_DURATION_HOURS, { optional: true }) ?? 5;
  // Optional — only available in shell context where AdminApi.Client is provided
  private readonly adminClient = inject(AdminApi.Client, { optional: true });
  private readonly injector = inject(Injector);
  private readonly lookupApiUrl = inject(LOOKUP_API_BASE_URL, { optional: true });

  private expiryTimerId: ReturnType<typeof setTimeout> | null = null;
  private isMonitoring = false;
  private preLogoutSaveHandler: (() => Promise<void>) | null = null;
  private previousOnlineState: boolean | null = null;
  private syncPromise: Promise<void> | null = null;
  private readonly syncedUserIds = new Set<string>();

  // ─── Reactive state ────────────────────────────────────────────────────────

  private readonly _offlineAuthenticated = signal<boolean>(false);

  /** Whether the user has passed offline PIN verification this session */
  readonly offlineAuthenticated = this._offlineAuthenticated.asReadonly();

  /** Whether the B2C token has expired */
  readonly tokenExpired = computed(() => {
    const expiresAt = this.authState.session().expiresAt;
    if (!expiresAt) return true;
    return new Date(expiresAt) <= new Date();
  });

  constructor() {
    // Keep offline cached userProfile up to date whenever authState has a valid userProfile
    effect(() => {
      const session = this.authState.session();
      if (
        session?.userProfile &&
        (session.userProfile.userName || session.userProfile.userId || session.userProfile.userEmail)
      ) {
        this.saveStoredUserProfile(session.userProfile);
      }
    });

    // Cache the last known JWT-derived roles/permissions/claims whenever they're real (source
    // is non-empty, i.e. actually decoded from a token — not the DEFAULT_CONTEXT fallback). An
    // offline PIN-restored session has no JWT to decode, so without this cache
    // AuthorizationService.context() would stay empty forever and hide all permission-gated UI.
    effect(() => {
      const context = this.authz.context();
      if (context.source && context.source !== 'impersonation') {
        saveOfflineAuthorizationContext({
          roles: context.roles,
          permissions: context.permissions,
          claims: context.claims,
        });
      }
    });

    // Setup reactive network monitoring effect safely within Angular injection context
    effect(() => {
      const isOnline = this.statusIndicator.isOnline();

      untracked(() => {
        if (!this.isMonitoring) {
          this.previousOnlineState = isOnline;
          return;
        }

        // Ignore if no change in online state
        if (this.previousOnlineState === isOnline) {
          return;
        }

        const wasOffline = this.previousOnlineState === false;
        this.previousOnlineState = isOnline;

        if (!isOnline) {
          this.onWentOffline();
        } else if (wasOffline) {
          // ONLY run onCameOnline if we were truly offline before!
          void this.onCameOnline();
        }
      });
    });
  }

  // ─── User Profile Cache helpers ───────────────────────────────────────────

  /** Retrieves stored user profile from persistent storage for offline usage */
  getStoredUserProfile(): UserProfile | null {
    const fromStorage = loadOfflineUserProfile();
    if (fromStorage && (fromStorage.userName || fromStorage.userId || fromStorage.userEmail)) {
      return fromStorage;
    }
    return null;
  }

  /** Persists user profile to storage so offline mode has full user details */
  saveStoredUserProfile(profile: UserProfile): void {
    if (profile && (profile.userName || profile.userId || profile.userEmail)) {
      saveOfflineUserProfile(profile);
      if (profile.userId) {
        try {
          localStorage.setItem('symphony.offline.lastUserId', profile.userId);
        } catch {}
      }
    }
  }

  // ─── Table init helpers ────────────────────────────────────────────────────

  private async ensureSessionTable(): Promise<void> {
    if (!this.store.isReady()) {
      await this.store.initialize().catch(() => {});
    }
    if (!this.store.tableExists(OFFLINE_SESSION_TABLE)) {
      await this.store
        .addTable(OFFLINE_SESSION_TABLE, { primaryKey: 'id', indexes: [], encrypted: false })
        .catch(() => {});
    }
  }

  private async ensurePinTable(): Promise<void> {
    if (!this.store.isReady()) {
      await this.store.initialize().catch(() => {});
    }
    if (!this.store.tableExists(OFFLINE_PIN_TABLE)) {
      await this.store
        .addTable(OFFLINE_PIN_TABLE, { primaryKey: 'userId', indexes: [], encrypted: false })
        .catch(() => {});
    }
  }

  private async readPinRecordFromNativeIdb(userId: string): Promise<any | null> {
    if (typeof indexedDB === 'undefined') return null;
    const dbNames = ['symphony-web-lookup', 'symphony-web-cache', 'symphony-web-assessment'];
    for (const dbName of dbNames) {
      try {
        const record = await new Promise<any>((resolve) => {
          const req = indexedDB.open(dbName);
          req.onerror = () => resolve(null);
          req.onblocked = () => resolve(null);
          req.onsuccess = () => {
            const db = req.result;
            try {
              if (!db.objectStoreNames.contains(OFFLINE_PIN_TABLE)) {
                db.close();
                return resolve(null);
              }
              const tx = db.transaction(OFFLINE_PIN_TABLE, 'readonly');
              const store = tx.objectStore(OFFLINE_PIN_TABLE);
              const getReq = store.get(userId);
              getReq.onsuccess = () => {
                if (getReq.result) {
                  db.close();
                  return resolve(getReq.result);
                }
                const allReq = store.getAll();
                allReq.onsuccess = () => {
                  db.close();
                  const records = (allReq.result as any[]) || [];
                  const found = records.find((r) => !!(r.hash || r.pinHash || r.pin));
                  resolve(found || null);
                };
                allReq.onerror = () => {
                  db.close();
                  resolve(null);
                };
              };
              getReq.onerror = () => {
                db.close();
                resolve(null);
              };
            } catch {
              try {
                db.close();
              } catch {}
              resolve(null);
            }
          };
        });
        if (record) return record;
      } catch {
        // continue
      }
    }
    return null;
  }

  private async saveSessionToNativeIdb(session: OfflineSession): Promise<void> {
    if (typeof indexedDB === 'undefined') return;
    await new Promise<void>((resolve) => {
      try {
        const req = indexedDB.open('symphony-web-lookup');
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
        req.onsuccess = () => {
          const db = req.result;
          try {
            if (!db.objectStoreNames.contains(OFFLINE_SESSION_TABLE)) {
              db.close();
              return resolve();
            }
            const tx = db.transaction(OFFLINE_SESSION_TABLE, 'readwrite');
            const store = tx.objectStore(OFFLINE_SESSION_TABLE);
            store.put(session);
            tx.oncomplete = () => {
              db.close();
              resolve();
            };
            tx.onerror = () => {
              db.close();
              resolve();
            };
          } catch {
            try {
              db.close();
            } catch {}
            resolve();
          }
        };
      } catch {
        resolve();
      }
    });
  }

  private async readSessionFromNativeIdb(): Promise<OfflineSession | undefined> {
    if (typeof indexedDB === 'undefined') return undefined;
    return new Promise<OfflineSession | undefined>((resolve) => {
      try {
        const req = indexedDB.open('symphony-web-lookup');
        req.onerror = () => resolve(undefined);
        req.onblocked = () => resolve(undefined);
        req.onsuccess = () => {
          const db = req.result;
          try {
            if (!db.objectStoreNames.contains(OFFLINE_SESSION_TABLE)) {
              db.close();
              return resolve(undefined);
            }
            const tx = db.transaction(OFFLINE_SESSION_TABLE, 'readonly');
            const store = tx.objectStore(OFFLINE_SESSION_TABLE);
            const getReq = store.get('current');
            getReq.onsuccess = () => {
              db.close();
              resolve(getReq.result);
            };
            getReq.onerror = () => {
              db.close();
              resolve(undefined);
            };
          } catch {
            try {
              db.close();
            } catch {}
            resolve(undefined);
          }
        };
      } catch {
        resolve(undefined);
      }
    });
  }

  private async deleteSessionFromNativeIdb(): Promise<void> {
    if (typeof indexedDB === 'undefined') return;
    return new Promise<void>((resolve) => {
      try {
        const req = indexedDB.open('symphony-web-lookup');
        req.onerror = () => resolve();
        req.onblocked = () => resolve();
        req.onsuccess = () => {
          const db = req.result;
          try {
            if (!db.objectStoreNames.contains(OFFLINE_SESSION_TABLE)) {
              db.close();
              return resolve();
            }
            const tx = db.transaction(OFFLINE_SESSION_TABLE, 'readwrite');
            const store = tx.objectStore(OFFLINE_SESSION_TABLE);
            store.delete('current');
            tx.oncomplete = () => {
              db.close();
              resolve();
            };
            tx.onerror = () => {
              db.close();
              resolve();
            };
          } catch {
            try {
              db.close();
            } catch {}
            resolve();
          }
        };
      } catch {
        resolve();
      }
    });
  }

  // ─── Pre-Logout Save Handlers ──────────────────────────────────────────────

  /** Register a callback to flush pending clinical assessment data before a forced offline logout. */
  registerPreLogoutSaveHandler(handler: () => Promise<void>): void {
    this.preLogoutSaveHandler = handler;
  }

  /** Unregister the pre-logout save handler. */
  unregisterPreLogoutSaveHandler(): void {
    this.preLogoutSaveHandler = null;
  }

  /** Flushes any registered pre-logout save handler to preserve work before session invalidation. */
  async flushPreLogoutSave(): Promise<void> {
    if (this.preLogoutSaveHandler) {
      try {
        await this.preLogoutSaveHandler();
      } catch (err) {
        console.error('[OfflineAuthService] Pre-logout save handler failed:', err);
      }
    }
  }

  // ─── Session management ────────────────────────────────────────────────────

  private get sessionDurationMs(): number {
    return (this.sessionDurationHours ?? 5) * 60 * 60 * 1000;
  }

  /** Create an offline session in IndexedDB with configurable duration (default 5 hours) */
  async createSession(): Promise<OfflineSession> {
    console.log('%c[OFFLINE-AUTH-SVC] createSession() starting...', 'color: #ff5722; font-weight: bold', {
      durationHours: this.sessionDurationHours,
      durationMs: this.sessionDurationMs,
    });

    const now = new Date();
    const expiresAt = new Date(now.getTime() + this.sessionDurationMs).toISOString();
    const userProfile = this.getStoredUserProfile() ?? this.authState.getSession().userProfile;
    const session: OfflineSession = {
      id: 'current',
      createdAt: now.toISOString(),
      expiresAt,
      userId: userProfile?.userId ?? this.getUserId(),
      userProfile: userProfile ?? undefined,
    };

    try {
      await this.ensureSessionTable();
      await this.store.put(OFFLINE_SESSION_TABLE, session);
    } catch (storeErr) {
      console.warn(
        '%c[OFFLINE-AUTH-SVC] store.put session error, falling back to native IDB:',
        'color: #ff9800',
        storeErr
      );
      await this.saveSessionToNativeIdb(session);
    }

    try {
      localStorage.setItem('symphony.offline.session', JSON.stringify(session));
    } catch {
      // ignore
    }

    this._offlineAuthenticated.set(true);
    this.authState.updateSession({
      status: AuthSessionStatus.SignedIn,
      expiresAt,
      lastActivityAt: now.toISOString(),
      userProfile: userProfile ?? undefined,
    });
    this.scheduleOfflineSessionExpiryTimer(expiresAt);

    console.log('%c[OFFLINE-AUTH-SVC] Offline session created & saved:', 'color: #4caf50; font-weight: bold', session);
    return session;
  }

  /** Check if a valid (non-expired) offline session exists in IndexedDB */
  async hasValidSession(): Promise<boolean> {
    let session: OfflineSession | undefined;
    try {
      await this.ensureSessionTable();
      session = await this.store.getByKey<OfflineSession>(OFFLINE_SESSION_TABLE, 'current');
    } catch {
      // ignore
    }

    if (!session) {
      session = await this.readSessionFromNativeIdb();
    }

    if (!session) {
      try {
        const local = localStorage.getItem('symphony.offline.session');
        if (local) session = JSON.parse(local);
      } catch {
        // ignore
      }
    }

    if (!session) {
      console.log('%c[OFFLINE-AUTH-SVC] hasValidSession: no session found', 'color: #ff9800');
      return false;
    }

    const isValid = new Date(session.expiresAt) > new Date();
    console.log('%c[OFFLINE-AUTH-SVC] hasValidSession check:', 'color: #ff5722', {
      createdAt: session.createdAt,
      expiresAt: session.expiresAt,
      isValid,
      now: new Date().toISOString(),
    });

    if (isValid && !this._offlineAuthenticated()) {
      console.log(
        '%c[OFFLINE-AUTH-SVC] Restoring offlineAuthenticated signal & authState from valid session',
        'color: #4caf50; font-weight: bold'
      );
      this._offlineAuthenticated.set(true);
      const userProfile = session.userProfile ?? this.getStoredUserProfile() ?? this.authState.getSession().userProfile;
      // Restore the last known JWT-derived permissions too — there's no token to decode offline,
      // so without this, AuthorizationService.context() stays empty and all permission-gated UI
      // (e.g. "Start Assessment" buttons) stays hidden even though the user is offline-authenticated.
      const cachedAuthContext = loadOfflineAuthorizationContext();
      this.authState.updateSession({
        status: AuthSessionStatus.SignedIn,
        expiresAt: session.expiresAt,
        lastActivityAt: new Date().toISOString(),
        userProfile: userProfile ?? undefined,
        permissions: cachedAuthContext?.permissions ?? this.authState.getSession().permissions,
      });
      this.scheduleOfflineSessionExpiryTimer(session.expiresAt);
    }
    return isValid;
  }

  /** Get the current offline session (may be expired or undefined) */
  async getSession(): Promise<OfflineSession | undefined> {
    let session: OfflineSession | undefined;
    try {
      await this.ensureSessionTable();
      session = await this.store.getByKey<OfflineSession>(OFFLINE_SESSION_TABLE, 'current');
    } catch {
      // ignore
    }
    if (!session) {
      session = await this.readSessionFromNativeIdb();
    }
    return session;
  }

  /** Clear the offline session from IndexedDB */
  async clearSession(): Promise<void> {
    console.log('%c[OFFLINE-AUTH-SVC] clearSession() called - removing session', 'color: #f44336; font-weight: bold');
    try {
      await this.ensureSessionTable();
      await this.store.delete(OFFLINE_SESSION_TABLE, 'current');
    } catch {
      // ignore
    }
    await this.deleteSessionFromNativeIdb();
    try {
      localStorage.removeItem('symphony.offline.session');
    } catch {
      // ignore
    }
    this.cancelExpiryTimer();
    this.deauthenticate();
  }

  // ─── PIN management ────────────────────────────────────────────────────────

  private getUserId(): string {
    const activeUserId =
      (this.authz.context().claims['userId'] as string | undefined) ??
      this.authState.getSession().userProfile?.userId ??
      this.authState.getSession().subjectId;

    if (activeUserId && activeUserId !== 'auth-disabled' && activeUserId !== 'offline-user') {
      try {
        localStorage.setItem('symphony.offline.lastUserId', activeUserId);
      } catch {
        // ignore
      }
      return activeUserId;
    }

    try {
      const lastUserId = localStorage.getItem('symphony.offline.lastUserId');
      if (lastUserId) return lastUserId;
    } catch {
      // ignore
    }

    const storedProfile = this.getStoredUserProfile();
    if (storedProfile?.userId && storedProfile.userId !== 'auth-disabled' && storedProfile.userId !== 'offline-user') {
      return storedProfile.userId;
    }

    return 'offline-user';
  }

  private async hashPin(pin: string, salt: string): Promise<string> {
    const data = new TextEncoder().encode(pin + salt);
    const hashBuffer = await crypto.subtle.digest('SHA-256', data);
    return Array.from(new Uint8Array(hashBuffer))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }

  /** Fetch the per-user salt from backend. Returns null when offline or adminClient unavailable. */
  private async getServerSalt(userId: string): Promise<string | null> {
    if (!this.adminClient) return null;
    const userIdNum = parseInt(userId, 10);
    if (isNaN(userIdNum)) return null;
    try {
      return await firstValueFrom(this.adminClient.saltKey(userIdNum).pipe(catchError(() => of(null))));
    } catch {
      return null;
    }
  }

  /**
   * Resolves salt for the user:
   *
   * 1. Checks IndexedDB cache first. If salt exists in IndexedDB, returns it immediately without any API call.
   * 2. If salt is not found in IndexedDB and network is online, fetches from backend via saltKey API.
   * 3. Persists the fetched salt in IndexedDB immediately so subsequent calls never hit the API.
   * 4. Returns deterministic fallback if offline or unreachable.
   */
  async resolveSalt(userId: string): Promise<string> {
    await this.ensurePinTable();
    const existingRecord = await this.store.getByKey<OfflinePinRecord>(OFFLINE_PIN_TABLE, userId);
    if (existingRecord?.salt) {
      return existingRecord.salt;
    }

    // Salt not in IndexedDB — only now query backend API
    if (navigator.onLine && this.adminClient) {
      const serverSalt = await this.getServerSalt(userId);
      if (serverSalt) {
        // Cache the salt in IndexedDB immediately!
        const recordToSave: OfflinePinRecord = {
          userId,
          hash: existingRecord?.hash ?? '',
          salt: serverSalt,
          createdAt: existingRecord?.createdAt ?? new Date().toISOString(),
          lastSyncedAt: existingRecord?.lastSyncedAt,
        };
        await this.store.put(OFFLINE_PIN_TABLE, recordToSave);
        return serverSalt;
      }
    }

    return `symphony-offline-pin-${userId}`;
  }

  /**
   * Sets the user's PIN:
   *
   * 1. Requires network connectivity (API-first).
   * 2. Hashes PIN with user's salt (resolved from IndexedDB cache first).
   * 3. Calls backend pinPOST.
   * 4. Only updates IndexedDB upon successful API response.
   */
  async setPin(pin: string): Promise<void> {
    const userId = this.getUserId();
    if (!navigator.onLine || !this.adminClient) {
      throw new Error('Network connection is required to update PIN.');
    }

    await this.ensurePinTable();
    // Check IndexedDB first for salt; only fetch from server if missing in IndexedDB
    const salt = await this.resolveSalt(userId);
    const hash = await this.hashPin(pin, salt);
    const now = new Date().toISOString();
    const record: OfflinePinRecord = { userId, hash, salt, createdAt: now, lastSyncedAt: now };

    // API must succeed before updating IndexedDB
    const request = new AdminApi.SetNursePractitionerPinRequest({ pinHash: hash });
    await firstValueFrom(this.adminClient.pinPOST(request));

    // Store in IndexedDB only AFTER server sync succeeds
    await this.store.put(OFFLINE_PIN_TABLE, record);
    this.syncedUserIds.add(userId);
  }

  /**
   * Proactively checks whether PIN is in IndexedDB; if missing, pulls from server and caches. Call on Shell Layout or
   * Appointment Dashboard initialization.
   */
  async syncPinIfMissing(): Promise<void> {
    if (!navigator.onLine || !this.adminClient) return;
    const userId = this.getUserId();
    if (!userId || userId === 'offline-user') return;

    // Fast-path: already checked or synced during this browser session
    if (this.syncedUserIds.has(userId)) {
      return;
    }

    if (this.syncPromise) {
      return this.syncPromise;
    }

    this.syncPromise = this.doSyncPinIfMissing(userId);
    try {
      await this.syncPromise;
    } finally {
      this.syncPromise = null;
    }
  }

  private async doSyncPinIfMissing(userId: string): Promise<void> {
    await this.ensurePinTable();
    const record = await this.store.getByKey<OfflinePinRecord>(OFFLINE_PIN_TABLE, userId);

    // If already stored with both hash and salt, nothing missing
    if (record?.hash && record?.salt) {
      this.syncedUserIds.add(userId);
      return;
    }

    // Check IndexedDB first for salt; only fetch from server if missing in IndexedDB
    const salt = await this.resolveSalt(userId);

    // If record already had a hash in IndexedDB, ensure it's saved with salt and done
    if (record?.hash) {
      this.syncedUserIds.add(userId);
      return;
    }

    // If record was already synced from server previously, skip calling pinGET again
    if (record?.lastSyncedAt) {
      this.syncedUserIds.add(userId);
      return;
    }

    try {
      const response = await firstValueFrom(this.adminClient.pinGET().pipe(catchError(() => of(null))));
      const pinHash = response?.pinHash;
      const now = new Date().toISOString();
      const newRecord: OfflinePinRecord = {
        userId,
        hash: pinHash ?? '',
        salt,
        createdAt: record?.createdAt ?? now,
        lastSyncedAt: now,
      };
      await this.store.put(OFFLINE_PIN_TABLE, newRecord);
      this.syncedUserIds.add(userId);
    } catch {
      // Non-fatal
    }
  }

  /** Fetch PIN hash from server and update local IndexedDB record. Called when user comes back online. */
  async syncPinFromServer(): Promise<void> {
    if (!this.adminClient || !navigator.onLine) return;
    const userId = this.getUserId();
    if (!userId || userId === 'offline-user') return;

    try {
      // Check IndexedDB first for salt; only fetch if missing in IndexedDB
      const salt = await this.resolveSalt(userId);
      const response = await firstValueFrom(this.adminClient.pinGET().pipe(catchError(() => of(null))));
      const pinHash = response?.pinHash;
      await this.ensurePinTable();
      const existingRecord = await this.store.getByKey<OfflinePinRecord>(OFFLINE_PIN_TABLE, userId);
      const now = new Date().toISOString();
      const record: OfflinePinRecord = {
        userId,
        hash: pinHash ?? '',
        salt,
        createdAt: existingRecord?.createdAt ?? now,
        lastSyncedAt: now,
      };
      await this.store.put(OFFLINE_PIN_TABLE, record);
      this.syncedUserIds.add(userId);
    } catch {
      // Non-fatal
    }
  }

  /** Verify a PIN against the stored hash */
  async verifyPin(pin: string): Promise<boolean> {
    const cleanPin = (pin || '').trim();
    const userId = this.getUserId();
    console.log('%c[OFFLINE-AUTH-SVC] verifyPin called for userId:', 'color: #ff5722; font-weight: bold', userId);

    let record: any = null;
    try {
      await this.ensurePinTable();
      record = await this.store.getByKey<any>(OFFLINE_PIN_TABLE, userId);
    } catch (err) {
      console.warn('%c[OFFLINE-AUTH-SVC] Dexie getByKey failed in verifyPin:', 'color: #ff9800', err);
    }

    if (!record?.hash && !record?.pinHash && !record?.pin) {
      try {
        const allRecords = await this.store.getAll<any>(OFFLINE_PIN_TABLE);
        record = allRecords
          .filter((r: any) => !!(r.hash || r.pinHash || r.pin))
          .sort((a: any, b: any) => (b.lastSyncedAt ?? '').localeCompare(a.lastSyncedAt ?? ''))[0];
      } catch (err) {
        console.warn('%c[OFFLINE-AUTH-SVC] Dexie getAll failed in verifyPin:', 'color: #ff9800', err);
      }
    }

    if (!record?.hash && !record?.pinHash && !record?.pin) {
      console.log('%c[OFFLINE-AUTH-SVC] Querying native IndexedDB fallback for PIN record...', 'color: #ff5722');
      record = await this.readPinRecordFromNativeIdb(userId);
    }

    if (!record) {
      console.warn('%c[OFFLINE-AUTH-SVC] verifyPin: no PIN record found anywhere in IndexedDB', 'color: #f44336');
      return false;
    }

    console.log('%c[OFFLINE-AUTH-SVC] Evaluating PIN against record:', 'color: #ff5722', {
      recordUserId: record.userId ?? record.nursePractitionerId,
      hasHash: !!record.hash,
      hasPinHash: !!record.pinHash,
      hasSalt: !!record.salt,
      hasSaltKey: !!record.saltKey,
      hasPlainPin: !!record.pin,
    });

    const expectedHash = String(record.hash || record.pinHash || '').trim();
    const salt = String(
      record.salt || record.saltKey || `symphony-offline-pin-${record.userId || record.nursePractitionerId || userId}`
    );

    // Check 1: Plain PIN match if stored unhashed
    if (record.pin && String(record.pin).trim() === cleanPin) {
      console.log('%c[OFFLINE-AUTH-SVC] Plain PIN matched!', 'color: #4caf50; font-weight: bold');
      return true;
    }

    if (expectedHash) {
      // Check 2: SHA-256(cleanPin + salt)
      const hash1 = await this.hashPin(cleanPin, salt);
      if (hash1.toLowerCase() === expectedHash.toLowerCase()) {
        console.log('%c[OFFLINE-AUTH-SVC] SHA-256(pin + salt) matched!', 'color: #4caf50; font-weight: bold');
        return true;
      }

      // Check 3: SHA-256(salt + cleanPin)
      const hash2 = await this.hashPin(salt, cleanPin);
      if (hash2.toLowerCase() === expectedHash.toLowerCase()) {
        console.log('%c[OFFLINE-AUTH-SVC] SHA-256(salt + pin) matched!', 'color: #4caf50; font-weight: bold');
        return true;
      }

      // Check 4: SHA-256(cleanPin) without salt
      const hash3 = await this.hashPin(cleanPin, '');
      if (hash3.toLowerCase() === expectedHash.toLowerCase()) {
        console.log('%c[OFFLINE-AUTH-SVC] SHA-256(pin) without salt matched!', 'color: #4caf50; font-weight: bold');
        return true;
      }
    }

    console.warn('%c[OFFLINE-AUTH-SVC] PIN verification failed - hashes did not match', 'color: #f44336');
    return false;
  }

  /** Whether a PIN has been stored for current user */
  async hasPin(): Promise<boolean> {
    const userId = this.getUserId();
    let hasRecord = false;
    try {
      await this.ensurePinTable();
      const record = await this.store.getByKey<any>(OFFLINE_PIN_TABLE, userId);
      if (record?.hash || record?.pinHash || record?.pin) {
        console.log('%c[OFFLINE-AUTH-SVC] hasPin: found record for current userId in Dexie:', 'color: #ff5722', userId);
        return true;
      }

      const allRecords = await this.store.getAll<any>(OFFLINE_PIN_TABLE);
      hasRecord = allRecords.some((r: any) => !!(r.hash || r.pinHash || r.pin));
    } catch (dexieErr) {
      console.warn(
        '%c[OFFLINE-AUTH-SVC] Dexie check in hasPin failed, checking native IDB:',
        'color: #ff9800',
        dexieErr
      );
    }

    if (!hasRecord) {
      const nativeRecord = await this.readPinRecordFromNativeIdb(userId);
      if (nativeRecord) {
        console.log('%c[OFFLINE-AUTH-SVC] hasPin: found record in native IndexedDB:', 'color: #4caf50', nativeRecord);
        return true;
      }
    }

    console.log('%c[OFFLINE-AUTH-SVC] hasPin result:', 'color: #ff5722', { userId, hasRecord });
    return hasRecord;
  }

  /** Remove stored PIN from IndexedDB */
  async clearPin(): Promise<void> {
    const userId = this.getUserId();
    console.log('%c[OFFLINE-AUTH-SVC] clearPin called for userId:', 'color: #f44336', userId);
    try {
      await this.ensurePinTable();
      await this.store.delete(OFFLINE_PIN_TABLE, userId);
    } catch {
      // ignore
    }
    this.syncedUserIds.delete(userId);
  }

  /** Verify PIN and set offlineAuthenticated signal if correct */
  async authenticate(pin: string): Promise<boolean> {
    console.log('%c[OFFLINE-AUTH-SVC] authenticate(pin) starting...', 'color: #ff5722; font-weight: bold');
    const cleanPin = (pin || '').trim();
    // If a custom PIN is stored in IndexedDB, verify against it
    const hasPinStored = await this.hasPin();
    if (hasPinStored) {
      console.log('%c[OFFLINE-AUTH-SVC] Custom PIN is stored in IndexedDB -> verifying...', 'color: #ff5722');
      const valid = await this.verifyPin(cleanPin);
      if (valid) {
        console.log(
          '%c[OFFLINE-AUTH-SVC] Custom PIN matches! Setting offlineAuthenticated to true',
          'color: #4caf50; font-weight: bold'
        );
        this._offlineAuthenticated.set(true);
      } else {
        console.warn('%c[OFFLINE-AUTH-SVC] Custom PIN mismatch!', 'color: #f44336');
      }
      return valid;
    }

    // No user PIN stored yet — check against environment default PIN (or '1234')
    const defaultPin = this.defaultPin || '1234';
    console.log('%c[OFFLINE-AUTH-SVC] No custom PIN stored -> checking against default PIN', 'color: #ff5722');
    if (cleanPin === defaultPin) {
      console.log(
        '%c[OFFLINE-AUTH-SVC] Default PIN matches! Setting offlineAuthenticated to true',
        'color: #4caf50; font-weight: bold'
      );
      this._offlineAuthenticated.set(true);
      return true;
    }

    console.warn('%c[OFFLINE-AUTH-SVC] Default PIN mismatch!', 'color: #f44336');
    return false;
  }

  /** Clear offline auth state — called when user comes back online */
  deauthenticate(): void {
    console.log(
      '%c[OFFLINE-AUTH-SVC] deauthenticate() called -> offlineAuthenticated = false',
      'color: #ff9800; font-weight: bold'
    );
    this._offlineAuthenticated.set(false);
  }

  // ─── Online/offline monitoring ─────────────────────────────────────────────

  /** Start monitoring network transitions — call from Layout.ngOnInit */
  startMonitoring(): void {
    if (this.isMonitoring) return;
    this.isMonitoring = true;
    this.previousOnlineState = this.statusIndicator.isOnline();
    console.log(
      '%c[OFFLINE-AUTH-SVC] startMonitoring() initialized, current online state:',
      'color: #ff5722; font-weight: bold',
      this.previousOnlineState
    );

    // Check current state immediately
    if (!this.previousOnlineState) {
      this.onWentOffline();
    }

    // navigator.onLine only reflects the network adapter, not real reachability (e.g. a
    // captive portal, VPN down, or DevTools blocking requests without toggling navigator.onLine).
    // Probe once now so the UI doesn't briefly show "online" before the browser's own event fires,
    // then only re-probe when navigator.onLine itself transitions — no continuous polling.
    this.runConnectivityProbe();

    if (typeof window !== 'undefined') {
      window.addEventListener('online', this.probeOnNetworkEvent);
      window.addEventListener('offline', this.probeOnNetworkEvent);
    }
  }

  /** Stop monitoring and cancel any pending expiry timers */
  stopMonitoring(): void {
    console.log('%c[OFFLINE-AUTH-SVC] stopMonitoring() called', 'color: #ff5722');
    this.isMonitoring = false;
    this.cancelExpiryTimer();
    if (typeof window !== 'undefined') {
      window.removeEventListener('online', this.probeOnNetworkEvent);
      window.removeEventListener('offline', this.probeOnNetworkEvent);
    }
  }

  private readonly probeOnNetworkEvent = (): void => this.runConnectivityProbe();

  private runConnectivityProbe(): void {
    void checkNetworkOnline(this.lookupApiUrl ?? undefined, true).then((isOnline) => {
      this.statusIndicator.reportProbeResult(isOnline);
    });
  }

  private onWentOffline(): void {
    console.warn('%c[OFFLINE-AUTH-SVC] *** TRANSITION TO OFFLINE DETECTED ***', 'color: #ff9800; font-weight: bold');
    this.cancelExpiryTimer();

    // Only track offline PIN session expiry if an offline PIN session is actively running
    void this.getSession().then((offlineSession) => {
      if (offlineSession?.expiresAt) {
        console.log(
          '%c[OFFLINE-AUTH-SVC] Tracking offline PIN session expiry at:',
          'color: #ff5722',
          offlineSession.expiresAt
        );
        this.scheduleOfflineSessionExpiryTimer(offlineSession.expiresAt);
      }
    });

    // NOTE: We do NOT revoke or clear JWT tokens or online session on network disconnection!
    // Fluctuations (brief network drops) must preserve the user's active session and tokens.
    // Token validity/expiry is strictly evaluated when coming back online.
  }

  private async onCameOnline(): Promise<void> {
    console.log('%c[OFFLINE-AUTH-SVC] *** TRANSITION TO ONLINE DETECTED ***', 'color: #4caf50; font-weight: bold');
    this.cancelExpiryTimer();
    this.deauthenticate();

    // 1. Clear the offline session from IndexedDB and localStorage
    const hadOfflineSession = (await this.getSession()) !== undefined;
    await this.clearSession();

    // 2. Evaluate JWT token status
    const session = this.authState.getSession();
    const token = session.customAccessToken || loadToken()?.accessToken;
    const expired = this.isTokenExpired(token, session.expiresAt);

    console.log('%c[OFFLINE-AUTH-SVC] onCameOnline status:', 'color: #4caf50', {
      hadOfflineSession,
      hasToken: !!token,
      isExpired: expired,
    });

    // If the token is expired, or if we had an offline session (no valid JWT),
    // or if the token is missing, obtain a new JWT token and continue.
    if (expired || hadOfflineSession || !token) {
      console.log(
        '%c[OFFLINE-AUTH-SVC] JWT token expired or missing after offline transition -> acquiring new JWT token...',
        'color: #ff9800; font-weight: bold'
      );
      await this.acquireNewJwtTokenAndContinue();
    } else {
      console.log(
        '%c[OFFLINE-AUTH-SVC] Existing JWT token is still valid -> continuing with current token without revocation',
        'color: #4caf50; font-weight: bold'
      );
    }

    // 3. Re-sync server PIN so local hash is up to date after coming back online
    void this.syncPinFromServer();
  }

  private isTokenExpired(token: string | null | undefined, expiresAtIso?: string | null): boolean {
    if (!token) {
      if (expiresAtIso) {
        const parsed = Date.parse(expiresAtIso);
        return isNaN(parsed) || parsed <= Date.now();
      }
      return true;
    }
    try {
      const payload = decodeJwt(token);
      if (payload && typeof payload['exp'] === 'number') {
        return payload['exp'] * 1000 <= Date.now();
      }
    } catch {
      // ignore
    }
    if (expiresAtIso) {
      const parsed = Date.parse(expiresAtIso);
      return isNaN(parsed) || parsed <= Date.now();
    }
    return false;
  }

  private async acquireNewJwtTokenAndContinue(): Promise<void> {
    try {
      const lifecycle = this.injector.get(ShellAuthLifecycleService, null, { optional: true });
      if (lifecycle && typeof lifecycle.refreshToken === 'function') {
        console.log('%c[OFFLINE-AUTH-SVC] Refreshing JWT token via lifecycle.refreshToken()...', 'color: #ff9800');
        await firstValueFrom(lifecycle.refreshToken());
        console.log(
          '%c[OFFLINE-AUTH-SVC] Successfully acquired new JWT token on coming online!',
          'color: #4caf50; font-weight: bold'
        );

        // If sitting on the /login route, navigate back to returnUrl or dashboard
        const currentUrl = this.router.url;
        if (currentUrl === '/login' || currentUrl.startsWith('/login?')) {
          const returnUrl = sessionStorage.getItem(OFFLINE_RETURN_URL_KEY) || '/';
          sessionStorage.removeItem(OFFLINE_RETURN_URL_KEY);
          await this.router.navigateByUrl(returnUrl);
        }
      }
    } catch (refreshErr) {
      console.warn(
        '%c[OFFLINE-AUTH-SVC] Silent token refresh failed on transition to online:',
        'color: #f44336',
        refreshErr
      );
      const lifecycle = this.injector.get(ShellAuthLifecycleService, null, { optional: true });
      const currentUrl = this.router.url;
      const isPublicPath = ['/login', '/unauthorized', '/offline', '/error'].some((p) => currentUrl.startsWith(p));
      if (lifecycle && typeof lifecycle.login === 'function' && !isPublicPath) {
        lifecycle.login();
      }
    }
  }

  private scheduleOfflineSessionExpiryTimer(expiresAtIso: string): void {
    this.cancelExpiryTimer();
    const timeUntilExpiry = Date.parse(expiresAtIso) - Date.now();
    console.log(
      `%c[OFFLINE-AUTH-SVC] scheduleOfflineSessionExpiryTimer: ${Math.round(timeUntilExpiry / 1000)}s until expiry (${expiresAtIso})`,
      'color: #ff5722'
    );
    if (timeUntilExpiry <= 0) {
      void this.handleOfflineSessionExpired();
    } else {
      this.expiryTimerId = setTimeout(() => {
        this.expiryTimerId = null;
        console.warn(
          '%c[OFFLINE-AUTH-SVC] Offline PIN session expired (5h TTL reached) -> logging off',
          'color: #f44336; font-weight: bold'
        );
        void this.handleOfflineSessionExpired();
      }, timeUntilExpiry);
    }
  }

  private async handleOfflineSessionExpired(): Promise<void> {
    console.warn(
      '%c[OFFLINE-AUTH-SVC] handleOfflineSessionExpired: logging off user to /login...',
      'color: #f44336; font-weight: bold'
    );
    // 1. Flush any active assessment changes before wiping session
    console.log('%c[OFFLINE-AUTH-SVC] Flushing pre-logout save handler...', 'color: #ff5722');
    await this.flushPreLogoutSave();

    const currentUrl = this.router.url;
    if (
      currentUrl &&
      currentUrl !== '/login' &&
      !currentUrl.startsWith('/unauthorized') &&
      !currentUrl.startsWith('/offline') &&
      !currentUrl.startsWith('/error')
    ) {
      console.log('%c[OFFLINE-AUTH-SVC] Storing returnUrl for post-login resume:', 'color: #ff5722', currentUrl);
      sessionStorage.setItem(OFFLINE_RETURN_URL_KEY, currentUrl);
    }

    // 2. Clear tokens and update state
    console.log('%c[OFFLINE-AUTH-SVC] Clearing session and updating authState to SignedOut', 'color: #f44336');
    clearSession();
    this.deauthenticate();
    this.authState.updateSession({
      status: AuthSessionStatus.SignedOut,
      subjectId: '',
      issuedAt: '',
      expiresAt: '',
      lastActivityAt: '',
      idleWarningAt: null,
      graceEndsAt: null,
      customAccessToken: '',
      permissions: [],
    });

    // 3. Clear IndexedDB offline session
    console.log('%c[OFFLINE-AUTH-SVC] Clearing offline session from IndexedDB', 'color: #f44336');
    await this.clearSession();

    // 4. Redirect to offline PIN login
    console.log('%c[OFFLINE-AUTH-SVC] Navigating to /login', 'color: #ff9800; font-weight: bold');
    await this.router.navigate(['/login']);
  }

  /**
   * Performs an explicit offline logout:
   *
   * 1. Flushes any registered pre-logout save handler (e.g. pending assessment work)
   * 2. Clears offline return URL and tab session markers so user starts fresh upon re-login
   * 3. Clears conductor and offline session tokens/storage
   * 4. Clears IndexedDB and localStorage offline session
   * 5. Resets authState to SignedOut
   * 6. Navigates cleanly to /login
   */
  async logoutOffline(): Promise<void> {
    console.log(
      '%c[OFFLINE-AUTH-SVC] logoutOffline() called - executing offline sign out',
      'color: #f44336; font-weight: bold'
    );
    try {
      await this.flushPreLogoutSave();
    } catch (err) {
      console.warn('[OfflineAuthService] Pre-logout save failed during offline logout:', err);
    }

    try {
      sessionStorage.removeItem(OFFLINE_RETURN_URL_KEY);
      sessionStorage.removeItem('auth_session_active');
    } catch {
      // ignore
    }

    // Note: deliberately NOT clearing the cached offline user profile (name/email/role) here —
    // it isn't PHI, and is needed to display the correct name after a subsequent offline PIN login.
    clearSession();
    clearToken();
    this.deauthenticate();
    this.authState.updateSession({
      status: AuthSessionStatus.SignedOut,
      subjectId: '',
      issuedAt: '',
      expiresAt: '',
      lastActivityAt: '',
      idleWarningAt: null,
      graceEndsAt: null,
      customAccessToken: '',
      idToken: '',
      permissions: [],
      userProfile: createEmptyUserProfile(),
    });

    await this.clearSession();

    console.log(
      '%c[OFFLINE-AUTH-SVC] Offline logout complete -> routing to /login',
      'color: #4caf50; font-weight: bold'
    );
    await this.router.navigate(['/login']);
  }

  private cancelExpiryTimer(): void {
    if (this.expiryTimerId !== null) {
      clearTimeout(this.expiryTimerId);
      this.expiryTimerId = null;
    }
  }
}
